import {
    ComponentFactoryResolver,
    Injectable,
    ApplicationRef,
    Injector,
    NgZone,
    Provider,
} from '@angular/core';
import {IOverlayState} from './overlay-state';
import {DomPortalHost} from './dom-portal-host';
import {OverlayRef} from './overlay-ref';
import {OverlayContainer, OVERLAY_CONTAINER_PROVIDER} from './overlay-container';


/** Next overlay unique ID. */
let nextUniqueId = 0;

/** The default state for newly created overlays. */
const defaultState: IOverlayState = {
    backdropClass: 'cdk-overlay-dark-backdrop',
    hasBackdrop: true
};


/**
 * Service to create Overlays. Overlays are dynamically added pieces of floating UI, meant to be
 * used as a low-level building building block for other components. Dialogs, tooltips, menus,
 * selects, etc. can all be built using overlays. The service should primarily be used by authors
 * of re-usable components rather than developers building end-user applications.
 *
 * An overlay *is* a PortalHost, so any kind of Portal can be loaded into one.
 */
@Injectable()
export class Overlay {
    constructor(private _overlayContainer: OverlayContainer,
                private _componentFactoryResolver: ComponentFactoryResolver,
                private _appRef: ApplicationRef,
                private _injector: Injector,
                private _ngZone: NgZone) {}

    /**
     * Creates an overlay.
     * @param state State to apply to the overlay.
     * @returns Reference to the created overlay.
     */
    create(state: IOverlayState = defaultState): OverlayRef {
        return this._createOverlayRef(this._createPaneElement(), state);
    }

    /**
     * Creates the DOM element for an overlay and appends it to the overlay container.
     * @returns Newly-created pane element
     */
    private _createPaneElement(): HTMLElement {
        const pane = document.createElement('div');
        pane.id = `cdk-overlay-${nextUniqueId++}`;
        pane.classList.add('cdk-overlay-pane');

        this._overlayContainer.getContainerElement().appendChild(pane);

        return pane;
    }

    /**
     * Create a DomPortalHost into which the overlay content can be loaded.
     * @param pane The DOM element to turn into a portal host.
     * @returns A portal host for the given DOM element.
     */
    private _createPortalHost(pane: HTMLElement): DomPortalHost {
        return new DomPortalHost(pane, this._componentFactoryResolver, this._appRef, this._injector);
    }

    /**
     * Creates an OverlayRef for an overlay in the given DOM element.
     * @param pane DOM element for the overlay
     * @param state
     */
    private _createOverlayRef(pane: HTMLElement, state: IOverlayState): OverlayRef {
        return new OverlayRef(this._createPortalHost(pane), pane, state, this._ngZone);
    }
}

/** Providers for Overlay and its related injectables. */
export const OVERLAY_PROVIDERS: Provider[] = [
    Overlay,
    OVERLAY_CONTAINER_PROVIDER,
];